import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Download, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatsTrendChart from "@/components/StatsTrendChart";

const SECTIONS = [
  ["vendor", "Vendors"],
  ["event_creator", "Event Creators"],
  ["marketplace_user", "Marketplace Users"],
];
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function AnalyticsTab() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [trendFor, setTrendFor] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/admin/analytics", { params: { month } })
      .then((r) => setData(r.data.roles))
      .catch((e) => toast.error(errMsg(e, "Could not load analytics")));
  }, [month]);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/admin/analytics/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "compass-kids-analytics.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e, "Download failed"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="analytics-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-bold text-forest/60 block mb-1">Month</label>
          <Input type="month" value={month} max={thisMonth()} onChange={(e) => e.target.value && setMonth(e.target.value)}
            className="rounded-xl bg-white w-44" data-testid="analytics-month-input" />
        </div>
        <Button onClick={download} disabled={downloading} className="rounded-full bg-forest hover:bg-forest/90 text-white" data-testid="analytics-download-btn">
          <Download className="w-4 h-4 mr-1.5" /> {downloading ? "Preparing..." : "Download CSV (all months)"}
        </Button>
      </div>
      {!data ? (
        <p className="text-sm text-forest/60" data-testid="analytics-loading">Loading…</p>
      ) : SECTIONS.map(([role, label]) => <RoleTable key={role} role={role} label={label} rows={data[role] || []} onTrend={setTrendFor} />)}
      <TrendDialog row={trendFor} month={month} onClose={() => setTrendFor(null)} />
    </div>
  );
}

function TrendDialog({ row, month, onClose }) {
  const [trend, setTrend] = useState(null);
  useEffect(() => {
    if (!row) return;
    setTrend(null);
    api.get(`/admin/analytics/trend/${row.user_id}`, { params: { month } }).then((r) => setTrend(r.data)).catch(() => setTrend([]));
  }, [row, month]);
  const keys = row?.role === "event_creator" ? ["clicks", "interests", "rsvps"] : ["clicks", "likes"];
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl sm:max-w-2xl" data-testid="analytics-trend-dialog">
        <DialogHeader>
          <DialogTitle className="text-forest">{row?.business_name || row?.name} — last 6 months</DialogTitle>
        </DialogHeader>
        {trend ? <StatsTrendChart data={trend} keys={keys} testid="analytics-trend-chart" /> : <p className="text-sm text-forest/60">Loading…</p>}
      </DialogContent>
    </Dialog>
  );
}

function RoleTable({ role, label, rows, onTrend }) {
  return (
    <div className="bg-white/80 border border-sage/20 rounded-3xl shadow-sm overflow-hidden" data-testid={`analytics-section-${role}`}>
      <h3 className="px-5 py-4 font-black text-forest border-b border-sage/10">{label} <span className="text-forest/40 font-semibold text-sm">({rows.length})</span></h3>
      {rows.length === 0 ? (
        <p className="px-5 py-6 text-sm text-forest/60">No {label.toLowerCase()} yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-softstone/50 text-forest/60 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-5 py-3">User</th>
                <th className="text-right px-5 py-3">Clicks</th>
                <th className="text-right px-5 py-3">Likes</th>
                <th className="text-right px-5 py-3">Interests</th>
                <th className="text-right px-5 py-3">RSVPs</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-t border-sage/10" data-testid={`analytics-row-${r.user_id}`}>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-forest">{r.business_name || r.name || "—"}</div>
                    <div className="text-[11px] text-forest/50">{r.email}</div>
                  </td>
                  <td className="px-5 py-3 text-right font-bold text-forest" data-testid={`analytics-clicks-${r.user_id}`}>{r.clicks}</td>
                  <td className="px-5 py-3 text-right font-bold text-forest" data-testid={`analytics-likes-${r.user_id}`}>{r.likes}</td>
                  <td className="px-5 py-3 text-right font-bold text-forest" data-testid={`analytics-interests-${r.user_id}`}>{r.interests}</td>
                  <td className="px-5 py-3 text-right font-bold text-forest" data-testid={`analytics-rsvps-${r.user_id}`}>{r.rsvps}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => onTrend(r)} className="inline-flex items-center gap-1 text-xs font-bold text-sage-dark hover:underline" data-testid={`analytics-trend-btn-${r.user_id}`}>
                      <TrendingUp className="w-3.5 h-3.5" /> Trend
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
